import React, { useState, useEffect } from 'react';
import { discountsApi } from '../services/api';
import '../styles/agencies.css';
import { extractErrorMessage } from '../utils/errorMessage';
import { useTranslation } from 'react-i18next';

interface Tier {
  tier_type: string;
  min_spend: number;
  discount_percent: number;
}

type SectionType = 'agency' | 'freelancer' | 'self_service';

// `key` is the stored tier_type — only the keys beside it are interface text.
const SECTIONS: { key: SectionType; labelKey: string; descKey: string; icon: React.ReactNode }[] = [
  {
    key: 'agency',
    labelKey: 'tiers.secAgencies',
    descKey: 'tiers.secAgenciesDesc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5z"/></svg>,
  },
  {
    key: 'freelancer',
    labelKey: 'tiers.secFreelancers',
    descKey: 'tiers.secFreelancersDesc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>,
  },
  {
    key: 'self_service',
    labelKey: 'tiers.secSelfService',
    descKey: 'tiers.secSelfServiceDesc',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>,
  },
];

const DiscountTiersPage: React.FC = () => {
  const { t } = useTranslation();
  const [tiersByType, setTiersByType] = useState<Record<string, Tier[]>>({
    agency: [],
    freelancer: [],
    self_service: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSection, setActiveSection] = useState<SectionType>('agency');

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const data = await discountsApi.getTiers();
      const legacy = data.tiers['agency_freelancer'] || [];
      setTiersByType({
        agency: data.tiers['agency'] || (legacy.length > 0 ? legacy : []),
        freelancer: data.tiers['freelancer'] || (legacy.length > 0 ? legacy : []),
        self_service: data.tiers['self_service'] || [],
      });
    } catch (err: any) {
      setError(extractErrorMessage(err, t('tiers.errLoadDiscount')));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const allTiers = [
        ...tiersByType.agency.map(t => ({ ...t, tier_type: 'agency' })),
        ...tiersByType.freelancer.map(t => ({ ...t, tier_type: 'freelancer' })),
        ...tiersByType.self_service.map(t => ({ ...t, tier_type: 'self_service' })),
      ];
      await discountsApi.updateTiers(allTiers);
      setSuccess(t('tiers.savedDiscount'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err, t('tiers.errSaveDiscount')));
    } finally {
      setSaving(false);
    }
  };

  const tiers = tiersByType[activeSection] || [];

  const setTiers = (newTiers: Tier[]) => {
    setTiersByType(prev => ({ ...prev, [activeSection]: newTiers }));
  };

  const addTier = () => {
    setTiers([...tiers, { tier_type: activeSection, min_spend: 0, discount_percent: 0 }]);
  };

  const removeTier = (index: number) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index: number, field: 'min_spend' | 'discount_percent', value: number) => {
    const updated = [...tiers];
    updated[index] = { ...updated[index], [field]: value };
    setTiers(updated);
  };

  const formatNumber = (n: number): string => {
    if (!n) return '0';
    return n.toLocaleString('ru-RU');
  };

  const parseFormattedNumber = (s: string): number => {
    const cleaned = s.replace(/[^\d]/g, '');
    return parseInt(cleaned, 10) || 0;
  };

  const section = SECTIONS.find(s => s.key === activeSection)!;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('tiers.discountTitle')}</h1>
          <p className="header-subtitle">{t('tiers.discountSubtitle')}</p>
        </div>
      </div>

      <div className="url-lists-layout">
        {/* Left Panel */}
        <aside className="url-lists-panel">
          {SECTIONS.map(s => (
            <button
              key={s.key}
              className={`settings-nav-item ${activeSection === s.key ? 'active' : ''}`}
              onClick={() => setActiveSection(s.key)}
            >
              {s.icon}
              {t(s.labelKey)}
            </button>
          ))}
        </aside>

        {/* Editor Area */}
        <div className="url-editor-panel">
          {loading ? (
            <div className="offices-loading">
              <div className="offices-spinner" />
            </div>
          ) : (
            <div className="settings-content">
              {/* Error / Success */}
              {error && (
                <div className="settings-message settings-message-error">
                  {error}
                  <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>&times;</button>
                </div>
              )}
              {success && (
                <div className="settings-message settings-message-success">{success}</div>
              )}

              <div className="settings-card">
                <h2 className="settings-card-title">{t('tiers.sectionTiers', { section: t(section.labelKey) })}</h2>
                <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 24px 0' }}>
                  {t(section.descKey)}
                </p>

                {tiers.map((tier, i) => (
                  <div key={i} className="discount-tier-row">
                    <div className="settings-form-group" style={{ flex: 1, marginBottom: 0 }}>
                      {i === 0 && <label className="field-label">{t('tiers.minSpend')}</label>}
                      <input
                        type="text"
                        className="settings-input"
                        value={formatNumber(tier.min_spend)}
                        onChange={e => updateTier(i, 'min_spend', parseFormattedNumber(e.target.value))}
                      />
                    </div>
                    <div className="settings-form-group" style={{ width: '120px', flexShrink: 0, marginBottom: 0 }}>
                      {i === 0 && <label className="field-label">{t('tiers.discountPercent')}</label>}
                      <input
                        type="number"
                        className="settings-input"
                        min={0}
                        max={40}
                        step={0.5}
                        value={tier.discount_percent}
                        onChange={e => updateTier(i, 'discount_percent', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <button
                      className="discount-tier-remove"
                      onClick={() => removeTier(i)}
                      title={t('tiers.removeTier')}
                      style={i === 0 ? { marginTop: '22px' } : undefined}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                ))}

                {tiers.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-secondary)', fontSize: '13px' }}>
                    {t('tiers.noTiers')}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button className="discount-tier-add" onClick={addTier}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    {t('tiers.addTier')}
                  </button>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    className="editor-empty-btn"
                    onClick={handleSave}
                    disabled={saving}
                    style={{ padding: '14px 48px' }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    {t(saving ? 'tiers.saving' : 'common.save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default DiscountTiersPage;
